"use client";

import { useState, useEffect, useCallback } from "react";

interface Props {
  token: string;
  isSuperuser?: boolean;
}

interface User {
  username: string;
  email: string;
  full_name: string;
  phone: string;
  role: string;
  active: boolean;
  odoo_partner_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

interface Invoice {
  id?: number;
  name: string;
  invoice_date: string;
  invoice_date_due?: string;
  amount_total: number;
  amount_residual: number;
  payment_state: string;
  state: string;
}

interface Balance {
  partner_id: number;
  outstanding_balance: number;
  open_lines: number;
}

interface OdooPartner {
  name: string;
  email?: string | false;
  phone?: string | false;
  street?: string | false;
  city?: string | false;
  zip?: string | false;
}

const paymentBadge = (state: string) => {
  const map: Record<string, string> = {
    paid: "bg-green-100 text-green-700",
    not_paid: "bg-yellow-100 text-yellow-700",
    partial: "bg-orange-100 text-orange-700",
    in_payment: "bg-blue-100 text-blue-700",
    reversed: "bg-gray-100 text-gray-500",
  };
  const labels: Record<string, string> = {
    paid: "Betalt",
    not_paid: "Ubetalt",
    partial: "Delvist",
    in_payment: "Under behandling",
    reversed: "Krediteret",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[state] ?? "bg-gray-100 text-gray-500"}`}>
      {labels[state] ?? state}
    </span>
  );
};

export default function UsersAdmin({ token, isSuperuser = false }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  // Create user
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", email: "", full_name: "", phone: "", role: "user" });
  const [creating, setCreating] = useState(false);

  // Selected user detail drawer
  const [selected, setSelected] = useState<User | null>(null);
  const [drawerTab, setDrawerTab] = useState<"profile" | "invoices" | "balance" | "password">("profile");

  // Edit profile
  const [editVals, setEditVals] = useState({ email: "", full_name: "", phone: "", role: "user" });
  const [saving, setSaving] = useState(false);

  // Password change
  const [newPwd, setNewPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");

  // Odoo sync
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  // Invoices
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceMonths, setInvoiceMonths] = useState(1);
  const [invoiceMsg, setInvoiceMsg] = useState("");

  // Balance
  const [balance, setBalance] = useState<Balance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Odoo partner
  const [partner, setPartner] = useState<OdooPartner | null>(null);

  const authH = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/users", { headers: authH() });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [authH]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const createUser = async () => {
    if (!newUser.username || !newUser.password) return;
    setCreating(true);
    setMsg("");
    try {
      const res = await fetch("/api/users", {
        method: "POST",
        headers: authH(),
        body: JSON.stringify(newUser),
      });
      if (res.ok) {
        setMsg("✅ Bruger oprettet");
        setShowCreate(false);
        setNewUser({ username: "", password: "", email: "", full_name: "", phone: "", role: "user" });
        await loadUsers();
      } else {
        const e = await res.json().catch(() => ({}));
        setMsg("❌ " + (e.detail ?? "Kunne ikke oprette"));
      }
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = async (u: User) => {
    if (!confirm(`Slet brugeren "${u.username}"? Kan ikke fortrydes.`)) return;
    const res = await fetch(`/api/users/${u.username}`, { method: "DELETE", headers: authH() });
    if (res.ok) {
      setMsg(`🗑️ "${u.username}" slettet`);
      if (selected?.username === u.username) setSelected(null);
      await loadUsers();
    } else {
      const e = await res.json().catch(() => ({}));
      setMsg("❌ " + (e.detail ?? "Fejl"));
    }
  };

  const openUser = (u: User) => {
    setSelected(u);
    setEditVals({ email: u.email, full_name: u.full_name, phone: u.phone, role: u.role });
    setDrawerTab("profile");
    setSyncMsg("");
    setInvoices([]);
    setBalance(null);
    setPartner(null);
    setNewPwd("");
    setPwdMsg("");
    setInvoiceMsg("");
  };

  const saveProfile = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/users/${selected.username}`, {
        method: "PUT",
        headers: authH(),
        body: JSON.stringify(editVals),
      });
      if (res.ok) {
        const data = await res.json();
        setSelected(data.user);
        setMsg("✅ Profil opdateret");
        await loadUsers();
      } else {
        const e = await res.json().catch(() => ({}));
        setMsg("❌ " + (e.detail ?? "Fejl"));
      }
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!selected || !newPwd) return;
    if (newPwd.length < 8) { setPwdMsg("❌ Min. 8 tegn"); return; }
    const res = await fetch(`/api/users/${selected.username}/change-password`, {
      method: "POST",
      headers: authH(),
      body: JSON.stringify({ new_password: newPwd }),
    });
    if (res.ok) { setPwdMsg("✅ Adgangskode ændret"); setNewPwd(""); }
    else { const e = await res.json().catch(() => ({})); setPwdMsg("❌ " + (e.detail ?? "Fejl")); }
  };

  const syncToOdoo = async () => {
    if (!selected) return;
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await fetch(`/api/users/${selected.username}/odoo/sync`, {
        method: "POST",
        headers: authH(),
      });
      if (res.ok) {
        const data = await res.json();
        setSyncMsg(`✅ Synkroniseret – Odoo partner #${data.odoo_partner_id}`);
        setSelected((s) => s ? { ...s, odoo_partner_id: data.odoo_partner_id } : s);
        await loadUsers();
        // fetch partner details
        const pRes = await fetch(`/api/users/${selected.username}/odoo/partner`, { headers: authH() });
        if (pRes.ok) { const pd = await pRes.json(); setPartner(pd.partner); }
      } else {
        const e = await res.json().catch(() => ({}));
        setSyncMsg("❌ " + (e.detail ?? "Odoo fejl"));
      }
    } finally {
      setSyncing(false);
    }
  };

  const loadInvoices = useCallback(async () => {
    if (!selected) return;
    setInvoicesLoading(true);
    try {
      const res = await fetch(`/api/users/${selected.username}/odoo/invoices`, { headers: authH() });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices ?? []);
      } else {
        const e = await res.json().catch(() => ({}));
        setInvoiceMsg("❌ " + (e.detail ?? "Ingen fakturaer"));
      }
    } finally {
      setInvoicesLoading(false);
    }
  }, [selected, authH]);

  const loadBalance = useCallback(async () => {
    if (!selected) return;
    setBalanceLoading(true);
    try {
      const res = await fetch(`/api/users/${selected.username}/odoo/balance`, { headers: authH() });
      if (res.ok) {
        const data = await res.json();
        setBalance(data);
      } else {
        const e = await res.json().catch(() => ({}));
        setInvoiceMsg("❌ " + (e.detail ?? "Saldo fejl"));
      }
    } finally {
      setBalanceLoading(false);
    }
  }, [selected, authH]);

  const loadPartner = useCallback(async () => {
    if (!selected?.odoo_partner_id) return;
    const res = await fetch(`/api/users/${selected.username}/odoo/partner`, { headers: authH() });
    if (res.ok) { const d = await res.json(); setPartner(d.partner); }
  }, [selected, authH]);

  useEffect(() => {
    if (drawerTab === "invoices" && selected?.odoo_partner_id) loadInvoices();
    if (drawerTab === "balance" && selected?.odoo_partner_id) { loadBalance(); loadPartner(); }
  }, [drawerTab, selected, loadInvoices, loadBalance, loadPartner]);

  const createSubscription = async () => {
    if (!selected) return;
    setCreatingInvoice(true);
    setInvoiceMsg("");
    try {
      const res = await fetch(`/api/users/${selected.username}/odoo/invoice/subscription`, {
        method: "POST",
        headers: authH(),
        body: JSON.stringify({ months: invoiceMonths }),
      });
      if (res.ok) {
        const data = await res.json();
        setInvoiceMsg(`✅ Faktura oprettet: ${data.invoice_number} – ${data.total} kr.`);
        await loadInvoices();
      } else {
        const e = await res.json().catch(() => ({}));
        setInvoiceMsg("❌ " + (e.detail ?? "Fejl"));
      }
    } finally {
      setCreatingInvoice(false);
    }
  };

  const postInvoice = async (invoiceId: number | undefined, invoiceName: string) => {
    if (!invoiceId || !selected) return;
    if (!confirm(`Bekræft faktura "${invoiceName}"? Den bliver forfaldsstillet.`)) return;
    const res = await fetch(`/api/users/${selected.username}/odoo/invoice/${invoiceId}/post`, {
      method: "POST",
      headers: authH(),
    });
    if (res.ok) {
      setInvoiceMsg(`✅ "${invoiceName}" bekræftet`);
      await loadInvoices();
    } else {
      const e = await res.json().catch(() => ({}));
      setInvoiceMsg("❌ " + (e.detail ?? "Fejl"));
    }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-14rem)]">
      {/* ── User list panel ── */}
      <div className="w-80 flex-shrink-0 flex flex-col gap-3 overflow-y-auto pr-1">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Brugere ({users.length})</h2>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="px-3 py-1.5 text-sm text-white rounded-lg font-medium"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            + Ny bruger
          </button>
        </div>

        {msg && (
          <div className="text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">{msg}</div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-800">Opret bruger</p>
            {[
              { key: "username", label: "Brugernavn*", type: "text" },
              { key: "password", label: "Adgangskode*", type: "password" },
              { key: "full_name", label: "Fuldt navn", type: "text" },
              { key: "email", label: "E-mail", type: "email" },
              { key: "phone", label: "Telefon", type: "tel" },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <label className="text-xs text-gray-500">{label}</label>
                <input
                  type={type}
                  value={(newUser as Record<string, string>)[key]}
                  onChange={(e) => setNewUser((p) => ({ ...p, [key]: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-green-400"
                />
              </div>
            ))}
            {isSuperuser && (
              <div>
                <label className="text-xs text-gray-500">Rolle</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
                >
                  <option value="user">Bruger</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={createUser}
                disabled={creating || !newUser.username || !newUser.password}
                className="flex-1 py-1.5 text-sm text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                {creating ? "Opretter..." : "Opret"}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Annuller
              </button>
            </div>
          </div>
        )}

        {/* User list */}
        {loading ? (
          <div className="text-sm text-gray-400 text-center py-8">Indlæser...</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">Ingen brugere</div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div
                key={u.username}
                onClick={() => openUser(u)}
                className={`bg-white border rounded-xl p-3 cursor-pointer transition-all ${
                  selected?.username === u.username
                    ? "border-green-400 ring-2 ring-green-100"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">{u.username}</span>
                      {u.role === "admin" && (
                        <span className="shrink-0 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Admin</span>
                      )}
                    </div>
                    {u.full_name && <p className="text-xs text-gray-500 truncate">{u.full_name}</p>}
                    {u.email && <p className="text-xs text-gray-400 truncate">{u.email}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {u.odoo_partner_id ? (
                        <span className="text-xs text-blue-600">🔗 Odoo #{u.odoo_partner_id}</span>
                      ) : (
                        <span className="text-xs text-gray-300">Ikke synkroniseret</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteUser(u); }}
                    className="text-red-400 hover:text-red-600 text-xs ml-2 mt-0.5 shrink-0"
                    title="Slet bruger"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Detail panel ── */}
      {selected ? (
        <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between bg-gray-50">
            <div>
              <h3 className="font-bold text-gray-900">{selected.username}</h3>
              <p className="text-xs text-gray-500">
                {selected.full_name || "–"} · {selected.role}
                {selected.odoo_partner_id && (
                  <span className="ml-2 text-blue-600">Odoo #{selected.odoo_partner_id}</span>
                )}
              </p>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
          </div>

          {/* Sub-tabs */}
          <div className="flex gap-1 px-4 pt-3 border-b border-gray-100">
            {([
              { id: "profile", label: "👤 Profil" },
              { id: "invoices", label: "🧾 Fakturaer" },
              { id: "balance", label: "💰 Saldo" },
              { id: "password", label: "🔒 Adgangskode" },
            ] as const).map((t) => (
              <button
                key={t.id}
                onClick={() => setDrawerTab(t.id)}
                className={`px-3 py-2 text-sm rounded-t-lg font-medium transition-colors ${
                  drawerTab === t.id
                    ? "bg-white border border-b-white border-gray-200 -mb-px text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {/* ── Profile tab ── */}
            {drawerTab === "profile" && (
              <div className="space-y-4 max-w-lg">
                {[
                  { key: "full_name", label: "Fuldt navn", type: "text" },
                  { key: "email", label: "E-mail", type: "email" },
                  { key: "phone", label: "Telefon", type: "tel" },
                ].map(({ key, label, type }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                    <input
                      type={type}
                      value={(editVals as Record<string, string>)[key]}
                      onChange={(e) => setEditVals((p) => ({ ...p, [key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                    />
                  </div>
                ))}
                {isSuperuser && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Rolle</label>
                    <select
                      value={editVals.role}
                      onChange={(e) => setEditVals((p) => ({ ...p, role: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
                    >
                      <option value="user">Bruger</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}

                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: "#2d6a4f" }}
                >
                  {saving ? "Gemmer..." : "💾 Gem profil"}
                </button>

                {/* Odoo sync section */}
                <div className="border-t border-gray-100 pt-4 mt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Odoo-konto</p>
                  <div className="flex items-center gap-3">
                    {selected.odoo_partner_id ? (
                      <span className="text-sm text-blue-600 font-medium">
                        🔗 Koblet til Odoo partner #{selected.odoo_partner_id}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Ikke synkroniseret endnu</span>
                    )}
                    <button
                      onClick={syncToOdoo}
                      disabled={syncing}
                      className="px-4 py-1.5 text-sm border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                    >
                      {syncing ? "⏳ Synkroniserer..." : "🔄 Synk til Odoo"}
                    </button>
                  </div>
                  {syncMsg && <p className="text-sm mt-2 text-gray-600">{syncMsg}</p>}
                </div>
              </div>
            )}

            {/* ── Invoices tab ── */}
            {drawerTab === "invoices" && (
              <div className="space-y-4">
                {!selected.odoo_partner_id ? (
                  <div className="text-center py-10 text-gray-400">
                    <p className="mb-2">Brugeren er ikke koblet til Odoo.</p>
                    <button
                      onClick={syncToOdoo}
                      disabled={syncing}
                      className="px-4 py-2 text-sm border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50"
                    >
                      {syncing ? "⏳" : "🔄 Synk til Odoo"}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Create subscription invoice */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                      <span className="text-sm text-gray-700">Abonnementsfaktura:</span>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={invoiceMonths}
                        onChange={(e) => setInvoiceMonths(Number(e.target.value))}
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none"
                      />
                      <span className="text-sm text-gray-500">måned(er)</span>
                      <button
                        onClick={createSubscription}
                        disabled={creatingInvoice}
                        className="ml-auto px-4 py-1.5 text-sm text-white rounded-lg font-medium disabled:opacity-50"
                        style={{ backgroundColor: "#2d6a4f" }}
                      >
                        {creatingInvoice ? "⏳" : "➕ Opret faktura"}
                      </button>
                    </div>

                    {invoiceMsg && (
                      <div className="text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">{invoiceMsg}</div>
                    )}

                    {invoicesLoading ? (
                      <div className="text-center py-8 text-gray-400 text-sm">Indlæser fakturaer...</div>
                    ) : invoices.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">Ingen fakturaer</div>
                    ) : (
                      <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Fakturanr.</th>
                              <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Dato</th>
                              <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Total</th>
                              <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Resterende</th>
                              <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Status</th>
                              <th className="px-4 py-2.5"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((inv, i) => (
                              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{inv.name}</td>
                                <td className="px-4 py-2.5 text-gray-500 text-xs">{inv.invoice_date}</td>
                                <td className="px-4 py-2.5 text-right font-medium">{inv.amount_total?.toFixed(2)} kr.</td>
                                <td className="px-4 py-2.5 text-right text-gray-500">{inv.amount_residual?.toFixed(2)} kr.</td>
                                <td className="px-4 py-2.5">{paymentBadge(inv.payment_state)}</td>
                                <td className="px-4 py-2.5">
                                  {inv.state === "draft" && (
                                    <button
                                      onClick={() => postInvoice(inv.id, inv.name)}
                                      className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-2 py-1 rounded"
                                    >
                                      Bekræft
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Balance tab ── */}
            {drawerTab === "balance" && (
              <div className="space-y-4">
                {!selected.odoo_partner_id ? (
                  <div className="text-center py-10 text-gray-400">
                    <p className="mb-2">Brugeren er ikke koblet til Odoo.</p>
                    <button onClick={syncToOdoo} disabled={syncing} className="px-4 py-2 text-sm border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50">
                      {syncing ? "⏳" : "🔄 Synk til Odoo"}
                    </button>
                  </div>
                ) : balanceLoading ? (
                  <div className="text-center py-8 text-gray-400 text-sm">Henter saldo...</div>
                ) : (
                  <>
                    {partner && (
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-xs text-gray-500 block">Navn</span><strong>{partner.name}</strong></div>
                        {partner.email && <div><span className="text-xs text-gray-500 block">E-mail</span>{partner.email}</div>}
                        {partner.phone && <div><span className="text-xs text-gray-500 block">Telefon</span>{partner.phone}</div>}
                        {partner.city && <div><span className="text-xs text-gray-500 block">By</span>{partner.city}</div>}
                      </div>
                    )}

                    {balance && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl border p-5 text-center ${balance.outstanding_balance > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}`}>
                          <p className="text-xs text-gray-500 mb-1">Udestående saldo</p>
                          <p className={`text-2xl font-bold ${balance.outstanding_balance > 0 ? "text-red-600" : "text-green-600"}`}>
                            {balance.outstanding_balance.toFixed(2)} kr.
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 text-center">
                          <p className="text-xs text-gray-500 mb-1">Åbne poster</p>
                          <p className="text-2xl font-bold text-gray-700">{balance.open_lines}</p>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => { loadBalance(); loadPartner(); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      🔄 Opdater
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── Password tab ── */}
            {drawerTab === "password" && (
              <div className="space-y-3 max-w-sm">
                <p className="text-sm text-gray-600">Admin kan sætte ny adgangskode uden at kende den nuværende.</p>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Ny adgangskode</label>
                  <input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && changePassword()}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                    placeholder="Min. 8 tegn"
                  />
                </div>
                <button
                  onClick={changePassword}
                  disabled={!newPwd}
                  className="px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-40"
                  style={{ backgroundColor: "#2d6a4f" }}
                >
                  🔒 Sæt adgangskode
                </button>
                {pwdMsg && <p className="text-sm text-gray-600">{pwdMsg}</p>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 bg-white border border-gray-200 rounded-xl flex items-center justify-center text-gray-300">
          <div className="text-center">
            <div className="text-5xl mb-3">👤</div>
            <p className="text-sm">Vælg en bruger for at se detaljer</p>
          </div>
        </div>
      )}
    </div>
  );
}
